import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import ImageUpload from '../../components/admin/ImageUpload';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const { getAuthHeaders } = useAdmin();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials`, getAuthHeaders());
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
    setLoading(false);
  };

  const handleEdit = (testimonial) => {
    setEditing(testimonial.id);
    setFormData(testimonial);
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.id || dataToSave.id === 'new') {
        dataToSave.id = crypto.randomUUID();
      }
      
      if (editing === 'new') {
        await axios.post(`${API}/admin/testimonials`, dataToSave, getAuthHeaders());
      } else {
        await axios.put(`${API}/admin/testimonials/${formData.id}`, dataToSave, getAuthHeaders());
      }
      await fetchTestimonials();
      setEditing(null);
      setFormData({});
      alert('Testimonial saved successfully!');
    } catch (error) {
      console.error('Error saving testimonial:', error);
      alert('Error saving testimonial: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await axios.delete(`${API}/admin/testimonials/${id}`, getAuthHeaders());
        await fetchTestimonials();
        alert('Testimonial deleted successfully!');
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        alert('Error deleting testimonial');
      }
    }
  };

  const handleNew = () => {
    setEditing('new');
    setFormData({
      id: '',
      quote: '',
      author: '',
      role: '',
      avatar: ''
    });
  };

  if (loading) return <AdminLayout><p className="text-white">Loading...</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="display-medium text-white">Testimonials Management</h1>
          <button onClick={handleNew} className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Add New Testimonial
          </button>
        </div>

        {editing && (
          <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
            <h2 className="heading-2 text-white mb-4">{editing === 'new' ? 'New Testimonial' : 'Edit Testimonial'}</h2>
            <div className="space-y-4">
              <div>
                <label className="body-small text-white mb-1 block">Quote</label>
                <textarea
                  value={formData.quote || ''}
                  onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                  className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded h-24"
                  placeholder="Enter testimonial quote"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="body-small text-white mb-1 block">Author Name</label>
                  <input
                    type="text"
                    value={formData.author || ''}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="body-small text-white mb-1 block">Role/Title</label>
                  <input
                    type="text"
                    value={formData.role || ''}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded"
                    placeholder="e.g., Product Manager"
                  />
                </div>
              </div>
              <div>
                <ImageUpload
                  label="Avatar Image"
                  value={formData.avatar}
                  onChange={(v) => setFormData({ ...formData, avatar: v })}
                  testId="testimonial-avatar"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                <Save size={20} /> Save
              </button>
              <button onClick={() => { setEditing(null); setFormData({}); }} className="btn-secondary flex items-center gap-2">
                <X size={20} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white/5 border border-white/25 p-6 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    {testimonial.avatar && (
                      <img src={testimonial.avatar} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover" />
                    )}
                    <div>
                      <h3 className="heading-3 text-white">{testimonial.author}</h3>
                      <p className="body-small text-white/60">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="body-medium text-white/85 italic">"{testimonial.quote}"</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(testimonial)} className="text-[#00FFD1] hover:text-[#6FD2C0]">
                    <Edit size={20} />
                  </button>
                  <button onClick={() => handleDelete(testimonial.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
